export function transformUsersList(restResponse: any): any {
  return {
    data: {
      listUsers: {
        items: (restResponse.users || []).map((u: any) => ({
          ...u,
          __typename: "User",
          institution: u.institution
            ? { ...u.institution, __typename: "Institution" }
            : null,
          programMemberships: {
            items: (u.programMemberships || []).map((pm: any) => ({
              ...pm,
              __typename: "ProgramMembership",
              program: pm.program
                ? {
                    ...pm.program,
                    __typename: "Program",
                    institution: pm.program.institution
                      ? { ...pm.program.institution, __typename: "Institution" }
                      : null
                  }
                : null
            })),
            nextToken: null,
            __typename: "ModelProgramMembershipConnection"
          }
        })),
        nextToken: restResponse.pagination?.nextOffset?.toString() || null,
        __typename: "ModelUserConnection"
      }
    }
  };
}

export function transformSearchUsers(restResponse: any): any {
  return {
    data: {
      searchUsers: {
        items: (restResponse.users || []).map((u: any) => ({
          ...u,
          __typename: "User",
          institution: u.institution
            ? { ...u.institution, __typename: "Institution" }
            : null,
          programMemberships: {
            items: (u.programMemberships || []).map((pm: any) => ({
              ...pm,
              __typename: "ProgramMembership",
              program: pm.program
                ? {
                    ...pm.program,
                    __typename: "Program",
                    institution: pm.program.institution
                      ? { ...pm.program.institution, __typename: "Institution" }
                      : null
                  }
                : null
            })),
            nextToken: null,
            __typename: "ModelProgramMembershipConnection"
          }
        })),
        nextToken: restResponse.pagination?.nextOffset?.toString() || null,
        __typename: "SearchableUserConnection"
      }
    }
  };
}

export function transformUser(restResponse: any): any {
  // Support either { user: {...} } or a flat object; minimally wrap to GraphQL shape
  const src = restResponse.user || restResponse;
  const programMembershipArray = src?.programMemberships || [];

  return {
    data: {
      getUser: {
        ...src,
        __typename: "User",
        institution: src?.institution
          ? { ...src.institution, __typename: "Institution" }
          : null,
        programMemberships: {
          items: (programMembershipArray || []).map((pm: any) => ({
            ...pm,
            __typename: "ProgramMembership"
          })),
          nextToken: null,
          __typename: "ModelProgramMembershipConnection"
        }
      }
    }
  };
}

export function transformProgramsList(restResponse: any): any {
  return {
    data: {
      listPrograms: {
        items: (restResponse.programs || []).map((p: any) => ({
          ...p,
          __typename: "Program",
          institution: p.institution
            ? { ...p.institution, __typename: "Institution" }
            : null
        })),
        nextToken: restResponse.pagination?.nextOffset?.toString() || null,
        __typename: "ModelProgramConnection"
      }
    }
  };
}

export function transformInstitutionsList(restResponse: any): any {
  return {
    data: {
      listInstitutions: {
        items: (restResponse.institutions || []).map((i: any) => ({
          ...i,
          __typename: "Institution"
        })),
        nextToken: restResponse.pagination?.nextOffset?.toString() || null,
        __typename: "ModelInstitutionConnection"
      }
    }
  };
}

export function transformNetworksList(restResponse: any): any {
  return {
    data: {
      listNetworks: {
        items: (restResponse.networks || []).map((n: any) => ({
          ...n,
          __typename: "Network",
          sponsor: n.sponsor ? { ...n.sponsor, __typename: "Sponsor" } : null,
          programs: n.programs
            ? {
                items: n.programs.map((p: any) => ({
                  ...p,
                  __typename: "Program"
                })),
                nextToken: null,
                __typename: "ModelProgramConnection"
              }
            : {
                items: [],
                nextToken: null,
                __typename: "ModelProgramConnection"
              }
        })),
        nextToken: restResponse.pagination?.nextOffset?.toString() || null,
        __typename: "ModelNetworkConnection"
      }
    }
  };
}

export function transformSiblingUsersForCreate(restResponse: any): any {
  // available-participants returns an array; normalize to GraphQL connection shape
  const items = Array.isArray(restResponse)
    ? restResponse
    : restResponse?.users || [];
  // Minimal User shape expected by Create flow: id, email, first/last/suffix, programMemberships.items.program
  const normalized = (items || []).map((u: any) => ({
    id: u.userId || u.id,
    email: u.email || null,
    fullName: u.fullName || null,
    initials: u.initials || null,
    firstName:
      u.firstName || (u.fullName ? String(u.fullName).split(" ")[0] : null),
    lastName: u.lastName || null,
    suffix: u.suffix || null,
    pgy: u.pgy ?? null,
    // Leave empty connection; Create flow only needs flat list + labels
    programMemberships: {
      items: [],
      nextToken: null,
      __typename: "ModelProgramMembershipConnection"
    },
    __typename: "User"
  }));
  return {
    data: {
      paginatableSiblingUsers: {
        items: normalized,
        nextToken: null,
        total: normalized.length,
        __typename: "PaginatableSiblingUsersResult"
      }
    }
  };
}

export function transformAssessmentsList(restResponse: any): any {
  return {
    data: {
      listAssessments: {
        items: (restResponse.assessments || []).map((a: any) => ({
          ...a,
          __typename: "Assessment",
          instrument: a.instrument
            ? { ...a.instrument, __typename: "Instrument" }
            : null,
          sponsor: a.sponsor ? { ...a.sponsor, __typename: "Sponsor" } : null,
          activities: a.activities
            ? { ...a.activities, __typename: "ModelActivityConnection" }
            : {
                items: [],
                nextToken: null,
                __typename: "ModelActivityConnection"
              },
          multiActivities: a.multiActivities
            ? {
                ...a.multiActivities,
                __typename: "ModelAssessmentActivityConnection"
              }
            : {
                items: [],
                nextToken: null,
                __typename: "ModelAssessmentActivityConnection"
              }
        })),
        nextToken: restResponse.pagination?.nextOffset?.toString() || null,
        __typename: "ModelAssessmentConnection"
      }
    }
  };
}

export function transformAssessment(restResponse: any): any {
  const asmt = restResponse.assessment || restResponse;
  // Normalize nested shapes with __typename hints
  const normalized = {
    ...asmt,
    __typename: "Assessment",
    instrument: asmt.instrument
      ? {
          ...asmt.instrument,
          __typename: "Instrument",
          questions: asmt.instrument.questions
            ? {
                ...asmt.instrument.questions,
                __typename: "ModelQuestionConnection"
              }
            : {
                items: [],
                nextToken: null,
                __typename: "ModelQuestionConnection"
              }
        }
      : null,
    sponsor: asmt.sponsor ? { ...asmt.sponsor, __typename: "Sponsor" } : null,
    activities: asmt.activities
      ? { ...asmt.activities, __typename: "ModelActivityConnection" }
      : { items: [], nextToken: null, __typename: "ModelActivityConnection" },
    multiActivities: asmt.multiActivities
      ? {
          ...asmt.multiActivities,
          __typename: "ModelAssessmentActivityConnection"
        }
      : {
          items: [],
          nextToken: null,
          __typename: "ModelAssessmentActivityConnection"
        }
  };
  return { data: { getAssessment: normalized } };
}

export function transformQuestionsList(
  restResponse: any,
  variables?: any
): any {
  const list = restResponse.questions || [];
  const items = (list || []).map((q: any) => ({
    ...q,
    __typename: "Question",
    // Normalize answerOptions as GraphQL connection shape if provided flat
    answerOptions:
      q.answerOptions && Array.isArray(q.answerOptions)
        ? {
            items: q.answerOptions.map((ao: any) => ({
              ...ao,
              __typename: "AnswerOption"
            })),
            nextToken: null,
            __typename: "ModelAnswerOptionConnection"
          }
        : q.answerOptions || {
            items: [],
            nextToken: null,
            __typename: "ModelAnswerOptionConnection"
          }
  }));
  return {
    data: {
      listQuestions: {
        items,
        nextToken: null,
        __typename: "ModelQuestionConnection"
      }
    }
  };
}

function normalizeProgram(program: any): any {
  if (!program) return null;
  return {
    ...program,
    __typename: "Program",
    institution: program.institution
      ? { ...program.institution, __typename: "Institution" }
      : null
  };
}

function normalizeInvitee(user: any): any {
  if (!user) {
    // Provide a non-null stub to prevent UI access errors
    return {
      id: null,
      email: null,
      emailLower: null,
      firstName: null,
      lastName: null,
      institution: null,
      programMemberships: {
        items: [],
        nextToken: null,
        __typename: "ModelProgramMembershipConnection"
      },
      __typename: "User"
    };
  }
  return {
    ...user,
    __typename: "User",
    institution: user.institution
      ? { ...user.institution, __typename: "Institution" }
      : null,
    programMemberships: user.programMemberships
      ? {
          ...user.programMemberships,
          __typename: "ModelProgramMembershipConnection"
        }
      : {
          items: [],
          nextToken: null,
          __typename: "ModelProgramMembershipConnection"
        }
  };
}

function unwrapMaybeIndexed(obj: any): any {
  // Some backends may return objects like { "0": { ... } } inside arrays; unwrap those
  if (obj && typeof obj === "object" && !Array.isArray(obj)) {
    const keys = Object.keys(obj);
    if (keys.length === 1 && /^\d+$/.test(keys[0])) {
      return obj[keys[0]];
    }
  }
  return obj;
}

function normalizeInvitation(inv: any): any {
  if (!inv) return null;
  inv = unwrapMaybeIndexed(inv);
  return {
    ...inv,
    __typename: "UserInvitation",
    program: normalizeProgram(inv.program),
    invitee: normalizeInvitee(inv.invitee)
  };
}

export function transformUserInvitationsList(restResponse: any): any {
  const list =
    restResponse.userinvitations || restResponse.userInvitations || [];
  return {
    data: {
      listUserInvitations: {
        items: (list || []).map((i: any) => normalizeInvitation(i)),
        nextToken: restResponse.pagination?.nextToken || null,
        __typename: "ModelUserInvitationConnection"
      }
    }
  };
}

export function transformSearchUserInvitations(restResponse: any): any {
  const list =
    restResponse.userinvitations || restResponse.userInvitations || [];
  const total =
    typeof restResponse.total === "number"
      ? restResponse.total
      : list?.length || 0;
  return {
    data: {
      searchUserInvitations: {
        items: (list || []).map((i: any) => normalizeInvitation(i)),
        nextToken: restResponse.pagination?.nextToken || null,
        total,
        __typename: "SearchableUserInvitationConnection"
      }
    }
  };
}

export function transformUserInvitation(restResponse: any): any {
  const inv =
    restResponse.userinvitation || restResponse.userInvitation || restResponse;
  const normalized = normalizeInvitation(inv);
  return {
    data: {
      getUserInvitation: normalized
    }
  };
}

function normalizeProgramMembership(pm: any): any {
  const programMember = pm.programMember
    ? {
        ...pm.programMember,
        __typename: "User",
        fullName: [pm.programMember.firstName, pm.programMember.lastName]
          .filter(Boolean)
          .join(" ")
          .trim() || null,
        institution: pm.programMember.institution
          ? { ...pm.programMember.institution, __typename: "Institution" }
          : null
      }
    : null;
  const program = pm.program
    ? {
        ...pm.program,
        __typename: "Program",
        institution: pm.program.institution
          ? { ...pm.program.institution, __typename: "Institution" }
          : null
      }
    : null;
  return {
    ...pm,
    __typename: "ProgramMembership",
    programMember,
    program
  };
}

export function transformProgramMembership(restResponse: any): any {
  const pm = restResponse;
  return {
    data: {
      getProgramMembership: pm ? normalizeProgramMembership(pm) : null
    }
  };
}

export function transformUsersByProgram(restResponse: any): any {
  const items = (restResponse.programmemberships || restResponse.programMemberships || []).map(
    (pm: any) => normalizeProgramMembership(pm)
  );
  return {
    data: {
      usersByProgram: {
        items,
        nextToken: restResponse.nextToken || null,
        __typename: "ModelProgramMembershipConnection"
      }
    }
  };
}
